/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

import React, { useState, useEffect, useRef, useMemo } from "react";
import { useTheme } from "../../theme/ThemeContext";

/**
 * FieldUIRulesAssistant
 * 
 * A conversational interface for configuring UI rules for a specific field.
 * 
 * ==========================================
 * JSON Structure of Field UI Rules:
 * ==========================================
 * {
 *   "visible": boolean,                  // Global visibility toggle
 *   "visible_if": {
 *      "role_in": ["Admin", "Manager"],  // Visible only to these roles
 *      "value_equals": { "status": "Active" } // Visible only if another field matches value
 *   },
 *   "readonly": boolean,                 // Global read-only toggle
 *   "readonly_if": {
 *      "role_in": ["Guest"],             // Read-only for these roles
 *      "value_equals": { "status": "Closed" }
 *   },
 *   "default": "some value",             // Static default value
 *   "default_expression": "now()",       // Dynamic default (e.g., now(), current_user.email)
 *   "options_source": {
 *      "type": "static" | "query",
 *      "values": [{ "label": "A", "value": "a" }] // Only if type=static
 *   },
 *   "hint_by_role": {
 *      "Admin": "Internal use only",
 *      "User": "Enter your display name"
 *   },
 *   "widget": {
 *      "placeholder": "Enter text..."
 *   }
 * }
 * ==========================================
 */
export default function FieldUIRulesAssistant({
    open,
    field,
    fields = [],
    roles = [],
    required = false,
    onClose,
    onSave
}) {
    const { theme } = useTheme();
    const scrollRef = useRef(null);

    // --- State Management (Replicating FieldUiRulesDialog) ---
    const [rulesState, setRulesState] = useState({
        visible: true,
        roleVisibleIn: [],
        visibleCondField: "",
        visibleCondValue: "",
        readonly: false,
        roleReadonlyIn: [],
        readonlyCondField: "",
        readonlyCondValue: "",
        defaultValue: "",
        defaultExpression: "",
        optionsSourceType: "none",
        staticOptions: [],
        roleOptionsByRole: {},
        roleHints: {},
        order: "",
        placeholder: ""
    });

    // Chat State
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState("");
    const [inputContext, setInputContext] = useState(null); // { key, subKey, type, label, ... }

    // Helpers
    const normalizedRoles = useMemo(() => (roles || []).map(r => typeof r === "string" ? { value: r, label: r } : r), [roles]);
    const roleValues = useMemo(() => normalizedRoles.map(r => r.value), [normalizedRoles]);

    // --- Initialization ---
    useEffect(() => {
        if (open && field) {
            // Reset chat
            setMessages([{
                id: Date.now(),
                sender: 'bot',
                text: `Hello! I'm here to help you configure the UI behavior for the field "${field.display_name || field.name}".\n\nI can help you set up:\n• Who can see or edit this field\n• Default values (Static or Dynamic)\n• Dropdown options\n• Helpful hints for users\n\nHow would you like to start?`,
                options: [{ label: "Show Configuration Menu", value: "show_main_menu" }]
            }]);

            // Parse existing UI rules into local state
            const ui = field.ui_rules || {};
            const newState = {
                visible: ui.visible !== false,
                roleVisibleIn: ui.visible_if?.role_in || [],
                visibleCondField: ui.visible_if?.value_equals ? Object.keys(ui.visible_if.value_equals)[0] || "" : "",
                visibleCondValue: ui.visible_if?.value_equals ? Object.values(ui.visible_if.value_equals)[0] || "" : "",

                readonly: ui.readonly === true,
                roleReadonlyIn: ui.readonly_if?.role_in || [],
                readonlyCondField: ui.readonly_if?.value_equals ? Object.keys(ui.readonly_if.value_equals)[0] || "" : "",
                readonlyCondValue: ui.readonly_if?.value_equals ? Object.values(ui.readonly_if.value_equals)[0] || "" : "",

                defaultValue: ui.default ?? "",
                defaultExpression: ui.default_expression ?? "",

                optionsSourceType: ui.options_source?.type || "none",
                staticOptions: ui.options_source?.type === "static" ? ui.options_source.values || [] : [],

                roleOptionsByRole: ui.options_by_role?.per_role || {},
                roleHints: ui.hint_by_role || {},

                order: ui.order ?? "",
                placeholder: ui.widget?.placeholder ?? ""
            };

            // Normalize condition values (if array, take first)
            if (Array.isArray(newState.visibleCondValue)) newState.visibleCondValue = newState.visibleCondValue[0];
            if (Array.isArray(newState.readonlyCondValue)) newState.readonlyCondValue = newState.readonlyCondValue[0];

            if (required) {
                newState.visible = true;
                newState.roleVisibleIn = [];
            }

            setRulesState(newState);
        }
    }, [open, field, required]);

    // Auto-scroll
    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages]);

    // --- Chat Logic ---

    const addBotMessage = (text, options = null) => {
        setMessages(prev => [...prev, { id: Date.now(), sender: 'bot', text, options }]);
    };

    const addUserMessage = (text) => {
        setMessages(prev => [...prev, { id: Date.now(), sender: 'user', text }]);
    };

    const handleOptionSelect = (option) => {
        if (!option) return;
        addUserMessage(option.label);
        processAction(option.value, option);
    };

    const handleTextSubmit = () => {
        if (inputValue === undefined || inputValue === null) return; // Allow empty string input
        addUserMessage(inputValue);

        if (inputContext) {
            if (inputContext.type === "state_update") {
                setRulesState(prev => ({ ...prev, [inputContext.key]: inputValue }));
                addBotMessage(`Updated ${inputContext.label}.`);
                setInputContext(null);
                setTimeout(() => showMainMenu(), 600);
            } else if (inputContext.type === "add_static_option_label") {
                setInputContext({ ...inputContext, type: 'add_static_option_value', tempLabel: inputValue, label: "Option Value" });
                addBotMessage("Now enter the value for this option:");
            } else if (inputContext.type === "add_static_option_value") {
                const newOpt = { label: inputContext.tempLabel, value: inputValue };
                setRulesState(prev => ({ ...prev, staticOptions: [...prev.staticOptions, newOpt] }));
                addBotMessage(`Added option: ${inputContext.tempLabel} (${inputValue})`);
                setInputContext(null);
                setTimeout(() => showOptionsMenu(), 600);
            }
        }
        setInputValue("");
    };

    // --- Menus & Flows ---

    const showMainMenu = (text = "What would you like to configure?") => {
        addBotMessage(text, [
            { label: "Visibility", value: "menu_visibility" },
            { label: "Read-Only", value: "menu_readonly" },
            { label: "Default Value", value: "menu_defaults" },
            { label: "Options/Choices", value: "menu_options" },
            { label: "Hints & Widget", value: "menu_hints" },
            { label: "Review & Save", value: "review" },
            { label: "Cancel", value: "close" }
        ]);
    };

    const showVisibilityMenu = () => {
        const status = rulesState.visible ? "Always Visible (unless restricted)" : "Hidden by Default";
        addBotMessage(`**Visibility Settings**\nControl when this field appears to users.\n\nCurrent State: **${status}**\n\nWhat would you like to change?`, [
            { label: `Toggle: ${rulesState.visible ? "Hide Completely" : "Make Visible"}`, value: "toggle_visible" },
            ...(rulesState.visible && !required ? [{ label: "Restrict by Role", value: "vis_roles" }] : []),
            ...(rulesState.visible && !required ? [{ label: "Conditional (Field Value)", value: "vis_cond" }] : []),
            { label: "« Back to Main Menu", value: "show_main_menu" }
        ]);
    };

    const showReadonlyMenu = () => {
        const status = rulesState.readonly ? "Always Read-Only" : "Editable (unless restricted)";
        addBotMessage(`**Read-Only Settings**\nControl when this field can be edited.\n\nCurrent State: **${status}**`, [
            { label: `Toggle: ${rulesState.readonly ? "Make Editable" : "Make Read-Only"}`, value: "toggle_readonly" },
            ...(!rulesState.readonly ? [{ label: "Restrict by Role (Read-Only for...)", value: "ro_roles" }] : []),
            ...(!rulesState.readonly ? [{ label: "Conditional (Field Value)", value: "ro_cond" }] : []),
            { label: "« Back to Main Menu", value: "show_main_menu" }
        ]);
    };

    const showDefaultsMenu = () => {
        addBotMessage(`**Default Values**\nAutomatically pre-fill this field.\n\n• **Static**: A fixed value (e.g., "Pending")\n• **Dynamic**: Calculated server-side (e.g., "now()", "current_user.email")\n\nCurrent Static: ${rulesState.defaultValue || "(None)"}\nCurrent Dynamic: ${rulesState.defaultExpression || "(None)"}`, [
            { label: "Set Static Default", value: "set_static_default" },
            { label: "Set Dynamic Expression", value: "set_dynamic_default" },
            { label: "Clear Defaults", value: "clear_defaults" },
            { label: "« Back to Main Menu", value: "show_main_menu" }
        ]);
    };

    const showOptionsMenu = () => {
        addBotMessage(`**Options Source**\nDefine where the choices for this field come from.\n\nCurrent Source: **${rulesState.optionsSourceType}**`, [
            { label: "No Options (Free Text)", value: "opt_src_none" },
            { label: "Static List (Manually Entered)", value: "opt_src_static" },
            { label: "Query (Dynamic)", value: "opt_src_query" },
            ...(rulesState.optionsSourceType === 'static' ? [{ label: "Manage Options List", value: "manage_static_opts" }] : []),
            { label: "« Back to Main Menu", value: "show_main_menu" }
        ]);
    };

    // --- Action Processor ---

    const processAction = (action, optionObj) => {
        switch (action) {
            case "show_main_menu": showMainMenu(); break;
            case "close": onClose(); break;

            // Visibility
            case "menu_visibility": showVisibilityMenu(); break;
            case "toggle_visible":
                setRulesState(prev => ({ ...prev, visible: !prev.visible }));
                setTimeout(showVisibilityMenu, 300);
                break;
            case "vis_roles":
                // In a real chatbot, we might list roles to toggle. For now, simple text input or list.
                // Let's ask for comma-separated roles or show a list? 
                // Showing list of roles as chips is better.
                addBotMessage("Select a role to toggle visibility for:",
                    roleValues.map(r => ({ label: r, value: `toggle_vis_role_${r}` })).concat([{ label: "Done", value: "menu_visibility" }])
                );
                break;

            // ReadOnly
            case "menu_readonly": showReadonlyMenu(); break;
            case "toggle_readonly":
                setRulesState(prev => ({ ...prev, readonly: !prev.readonly }));
                setTimeout(showReadonlyMenu, 300);
                break;

            // Defaults
            case "menu_defaults": showDefaultsMenu(); break;
            case "set_static_default":
                setInputContext({ type: "state_update", key: "defaultValue", label: "Static Default Value" });
                addBotMessage("Please enter the **Static Default Value**. This value will be pre-filled in the form.");
                break;
            case "set_dynamic_default":
                setInputContext({ type: "state_update", key: "defaultExpression", label: "Dynamic Expression" });
                addBotMessage("Please enter a **Dynamic Expression**.\n\nExamples:\n• `now()` - Current timestamp\n• `current_user.email` - User's email\n• `current_user.username` - User's name\n\nThis will be evaluated by the server.");
                break;
            case "clear_defaults":
                setRulesState(prev => ({ ...prev, defaultValue: "", defaultExpression: "" }));
                addBotMessage("Default values cleared.");
                setTimeout(showDefaultsMenu, 500);
                break;

            // Options
            case "menu_options": showOptionsMenu(); break;
            case "opt_src_static":
                setRulesState(prev => ({ ...prev, optionsSourceType: "static" }));
                setTimeout(showOptionsMenu, 300);
                break;
            case "opt_src_none":
                setRulesState(prev => ({ ...prev, optionsSourceType: "none", staticOptions: [] }));
                setTimeout(showOptionsMenu, 300);
                break;
            case "manage_static_opts":
                addBotMessage(`Current Options: ${rulesState.staticOptions.length}`, [
                    { label: "Add Option", value: "add_static_opt" },
                    { label: "Clear All", value: "clear_static_opts" },
                    { label: "Back", value: "menu_options" }
                ]);
                break;
            case "add_static_opt":
                setInputContext({ type: "add_static_option_label", label: "Option Label" });
                addBotMessage("Enter the Label for the new option:");
                break;

            // Save
            case "review":
                handleSave();
                break;

            default:
                // Handle dynamic role toggles
                if (action.startsWith("toggle_vis_role_")) {
                    const role = action.replace("toggle_vis_role_", "");
                    const current = rulesState.roleVisibleIn;
                    const exists = current.includes(role);
                    const newRoles = exists ? current.filter(r => r !== role) : [...current, role];
                    setRulesState(prev => ({ ...prev, roleVisibleIn: newRoles }));
                    addBotMessage(`${exists ? "Removed" : "Added"} role: ${role}`);
                    // Re-show role list
                    setTimeout(() => processAction("vis_roles"), 400);
                } else {
                    showMainMenu();
                }
                break;
        }
    };

    const constructFinalUiRules = () => {
        const s = rulesState;
        const ui = {};

        // Mapping local state back to JSON structure (same logic as Dialog)
        ui.visible = s.visible;
        if (!required && (s.roleVisibleIn.length > 0 || (s.visibleCondField && s.visibleCondValue))) {
            ui.visible_if = {};
            if (s.roleVisibleIn.length > 0) ui.visible_if.role_in = s.roleVisibleIn;
            if (s.visibleCondField) ui.visible_if.value_equals = { [s.visibleCondField]: s.visibleCondValue };
        }

        ui.readonly = s.readonly;
        if (s.roleReadonlyIn.length > 0 || (s.readonlyCondField && s.readonlyCondValue)) {
            ui.readonly_if = {};
            if (s.roleReadonlyIn.length > 0) ui.readonly_if.role_in = s.roleReadonlyIn;
            if (s.readonlyCondField) ui.readonly_if.value_equals = { [s.readonlyCondField]: s.readonlyCondValue };
        }

        if (s.defaultValue) ui.default = s.defaultValue;
        if (s.defaultExpression) ui.default_expression = s.defaultExpression;

        if (s.optionsSourceType !== 'none') {
            ui.options_source = { type: s.optionsSourceType };
            if (s.optionsSourceType === 'static') ui.options_source.values = s.staticOptions;
        }

        if (s.order) ui.order = Number(s.order);
        if (s.placeholder) ui.widget = { placeholder: s.placeholder };

        return ui;
    };

    const handleSave = () => {
        const rules = constructFinalUiRules();
        onSave(rules);
        addBotMessage("Rules Configured! Saving...");
        setTimeout(onClose, 800);
    };


    if (!open) return null;

    return (
        <>
            <div
                style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.3)", zIndex: 1040 }}
                onClick={onClose}
            />
            <div className="ui-rules-assistant-panel" style={{
                position: "fixed", top: 0, right: 0, bottom: 0, background: "#fff",
                boxShadow: "-4px 0 24px rgba(0,0,0,0.15)", zIndex: 1050, display: "flex", flexDirection: "column",
                animation: "slideInRight 0.3s ease-out", fontFamily: theme.fontFamily
            }}>
                {/* Header */}
                <div style={{
                    padding: "16px 20px", borderBottom: "1px solid #eaeaea", background: theme.primary, color: "#fff",
                    display: "flex", alignItems: "center", gap: 12, boxShadow: "0 2px 4px rgba(0,0,0,0.05)"
                }}>
                    <div style={{ width: 32, height: 32, borderRadius: "50%", background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <i className="bi bi-gear-fill" style={{ fontSize: "1.1rem" }} />
                    </div>
                    <div style={{ flex: 1 }}>
                        <h4 style={{ margin: 0, fontSize: "1rem", fontWeight: 600 }}>Field Assistant</h4>
                        <div style={{ fontSize: "0.75rem", opacity: 0.9 }}>{field?.display_name || field?.name}</div>
                    </div>
                    <button onClick={onClose} style={{ border: "none", background: "none", color: "#fff", fontSize: "1.2rem", cursor: "pointer", padding: 4 }}><i className="bi bi-x-lg" /></button>
                </div>

                {/* Chat Area */}
                <div ref={scrollRef} style={{ flex: 1, overflowY: "auto", padding: "20px", background: "#f4f6f9", display: "flex", flexDirection: "column", gap: 16 }}>
                    {messages.map((msg) => (
                        <div key={msg.id} style={{ alignSelf: msg.sender === 'user' ? "flex-end" : "flex-start", maxWidth: "85%" }}>
                            <div style={{
                                padding: "10px 14px",
                                borderRadius: msg.sender === 'user' ? "12px 12px 0 12px" : "12px 12px 12px 0",
                                background: msg.sender === 'user' ? theme.primary : "#fff",
                                color: msg.sender === 'user' ? "#fff" : theme.text,
                                boxShadow: "0 1px 2px rgba(0,0,0,0.1)", fontSize: "0.92rem", whiteSpace: "pre-wrap",
                                border: msg.sender === 'bot' ? "1px solid #e1e4e8" : "none"
                            }}>
                                {msg.text}
                            </div>
                            {msg.options && (
                                <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 6 }}>
                                    {msg.options.map((opt, i) => (
                                        <button key={i} onClick={() => handleOptionSelect(opt)}
                                            style={{
                                                padding: "6px 12px", borderRadius: 16, border: `1px solid ${theme.primary}`,
                                                background: "#fff", color: theme.primary, fontSize: "0.8rem", fontWeight: 500, cursor: "pointer"
                                            }}
                                            onMouseOver={(e) => { e.currentTarget.style.background = theme.primary; e.currentTarget.style.color = "#fff"; }}
                                            onMouseOut={(e) => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.color = theme.primary; }}
                                        >
                                            {opt.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {/* Input Area */}
                <div style={{ padding: "16px", background: "#fff", borderTop: "1px solid #eaeaea" }}>
                    <div style={{ display: "flex", gap: 10 }}>
                        <input
                            type="text" value={inputValue} onChange={(e) => setInputValue(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleTextSubmit()}
                            placeholder={inputContext ? `Enter ${inputContext.label}...` : "Select an option from above..."}
                            disabled={!inputContext}
                            style={{
                                flex: 1, padding: "10px 14px", borderRadius: 20, border: "1px solid #ced4da", outline: "none",
                                fontSize: "0.95rem", background: inputContext ? "#fff" : "#f8f9fa", cursor: inputContext ? "text" : "default"
                            }}
                        />
                        <button onClick={handleTextSubmit} disabled={!inputContext}
                            style={{
                                width: 40, height: 40, borderRadius: "50%", background: (!inputContext) ? "#e9ecef" : theme.primary,
                                color: "#fff", border: "none", display: "flex", alignItems: "center", justifyContent: "center",
                                cursor: (!inputContext) ? "default" : "pointer", transition: "all 0.2s"
                            }}
                        >
                            <i className="bi bi-send-fill" style={{ fontSize: "1rem" }} />
                        </button>
                    </div>
                </div>

                <style>{`
                    .ui-rules-assistant-panel {
                        width: 400px;
                    }
                    @media (max-width: 480px) {
                        .ui-rules-assistant-panel {
                            width: 100vw;
                            box-shadow: none; 
                        }
                    }
                    @keyframes slideInRight { 
                        from { transform: translateX(100%); opacity: 0; } 
                        to { transform: translateX(0); opacity: 1; } 
                    }
                `}</style>
            </div>
        </>
    );
}
